import { TextField, Tooltip } from '@mui/material';
import { useRef, useState } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import ContextMenuWithSubmenu from './ContextMenuWithSubmenu';

type PlaceholderItem = string;
type PlaceholderGroup = {
  label: string;
  items: (PlaceholderItem | PlaceholderGroup)[];
};

type Props = {
  name: string;
  label?: string;
  disabled?: boolean;
  rows?: number;
  tooltip?: string;
  rules?: RegisterOptions; // Accepts validation rules
  required?: boolean;
  defaultValue?: string | number;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  type?: 'text' | 'number' | 'textarea' | 'password';
  shrinkLabel?: boolean; // Optional prop to control label shrink
  placeholders?: (PlaceholderItem | PlaceholderGroup)[]; //  pass available placeholders
};

const VNTextField = ({
  name,
  label = '',
  disabled = false,
  rows = 0,
  tooltip = '',
  rules,
  required = false,
  defaultValue,
  type,
  onBlur,
  shrinkLabel = false,
  placeholders = [],
}: Props) => {
  const { control } = useFormContext();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // special validation for numbers
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'number') {
      const { key } = e;
      const isNumberKey = /^[0-9]$/.test(key);
      const isControls = [
        'Backspace',
        'Delete',
        'Enter',
        'Shift',
        'Control',
        'Alt',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
      ].includes(key);

      if (!isNumberKey && !isControls) {
        e.preventDefault();
        return false;
      }
    }
    return true;
  };

  // --- Open context menu on right-click
  // Inside VNTextField component
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    if (!placeholders.length) return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setMenuPosition({ top: event.clientY, left: event.clientX });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuPosition(null);
  };

  const handleInsert = (placeholder: string, value: string, onChange: (v: string) => void) => {
    handleCloseMenu();
    if (!inputRef.current) return;

    const cursor = inputRef.current.selectionStart ?? value.length;
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const newValue = `${before}${placeholder}${after}`;
    onChange(newValue);

    requestAnimationFrame(() => {
      if (inputRef.current) {
        const newPos = cursor + placeholder.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    });
  };

  return (
    <div className="vn-textfield">
      <Controller
        name={name}
        control={control}
        rules={{
          required: required && 'This is a required field',
          ...(type === 'number' && {
            min: { value: 0, message: 'Value must be a positive number' }, // Positive number validation
          }),
          ...rules,
        }}
        defaultValue={defaultValue}
        render={({ field: { value, onChange, ...field }, fieldState }) => {
          // Check if value is an array and convert it into a comma-separated string
          const isArray = Array.isArray(value);
          const textValue = isArray ? value.join(', ') : value || '';

          return (
            <Tooltip title={tooltip} placement="top-end" arrow>
              <>
                <TextField
                  variant="outlined"
                  size="small"
                  {...field}
                  id={name}
                  label={`${label}${required ? ' *' : ''}`}
                  disabled={disabled}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  multiline={rows > 0}
                  rows={rows}
                  type={type}
                  value={textValue}
                  onBlur={onBlur}
                  onContextMenu={handleContextMenu}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    onChange(isArray ? newValue.split(',').map((item) => item.trim()) : newValue);
                  }}
                  onKeyDown={handleKeyDown}
                  InputLabelProps={shrinkLabel ? { shrink: true } : {}}
                  inputRef={inputRef}
                />
                <ContextMenuWithSubmenu
                  anchorEl={anchorEl}
                  position={menuPosition}
                  onClose={handleCloseMenu}
                  placeholders={placeholders}
                  onInsert={(placeholder) => handleInsert(placeholder, textValue, onChange)}
                />
              </>
            </Tooltip>
          );
        }}
      />
    </div>
  );
};

export default VNTextField;
